import {
  Button,
  ButtonLink,
  Header,
  Icon,
  Stack,
  StackItem,
  Text
} from '@asgard/ui-library';
import { faCheckCircle as faCheckCircleLight } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { HashAnchor } from 'components/HashAnchor';
import Page from 'components/layout/Page';
import Checkbox from 'components/shared/Checkbox';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-scroll';
import { ServiceLead } from 'services/service.lead';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getErrorMessage } from 'utils/errors';
import * as S from './MagnetCTOPage.styled';
import {
  MagnetOnboardingForm,
  MagnetOnboardingFormSchema
} from './MagnetCTOPage.types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const MagnetCTOPage: React.FC = () => {
  const { t } = useI18n('magnets');
  const { addErrorToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [terms, setTerms] = useState(false);
  const [accept, setAccept] = useState(false);

  const formMethods = useForm<MagnetOnboardingForm>({
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      email: '',
      company: ''
    },
    resolver: zodResolver(MagnetOnboardingFormSchema)
  });

  const reason = formMethods.watch('reason');

  const onSubmit = useCallback(
    async (data: MagnetOnboardingForm) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha('magnets_cto_submit');

        ServiceLead.create({
          clientName: data.fullName,
          contactEmail: data.email,
          companyName: data.company,
          landingName: 'magnets-cto', // Note: landing url slug https://www.getmanfred.com/resource-cto
          isMql: data.reason === '1' || data.reason === '2',
          createPipedriveLead: data.reason === '1' || data.reason === '2',
          isSubscribeToNewsletter: true,
          recaptchaToken
        });

        setIsSubmitted(true);
      } catch (ex) {
        console.error(`[MagnetCTOPage] Could not send form data. ${ex}`);

        addErrorToast(
          t('magnets.cto.form.submit-error', {
            error: getErrorMessage(ex)
          })
        );
      }
    },
    [addErrorToast, t, executeRecaptcha]
  );

  return (
    <Page pageVariant="midnight_light">
      <S.StyledWrapperBG>
        <S.StyledWrapper>
          <Stack direction="column" gap="s" className="content">
            <Stack direction="column" gap="s" className="wrapper">
              <Text bold uppercase size="s">
                {t('magnets.cto.content.sub-title')}
              </Text>
              <Header as="h2" size="4xl">
                {t('magnets.cto.content.title')}
              </Header>

              <HtmlTrans ns="magnets" i18nKey="magnets.cto.content.desc" />
            </Stack>
          </Stack>

          <StackItem className="form">
            {!isSubmitted ? (
              <FormProvider {...formMethods}>
                <HashAnchor name="form" />
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <Stack direction="column" gap="4xl">
                    <FormText
                      name="fullName"
                      formGroup={{
                        label: t('magnets.cto.form.full-name.label')
                      }}
                    />

                    <FormText
                      name="email"
                      formGroup={{
                        label: t('magnets.cto.form.email.label')
                      }}
                    />

                    <FormText
                      name="company"
                      formGroup={{
                        label: t('magnets.cto.form.company.label')
                      }}
                    />

                    <FormSelect
                      name="reason"
                      formGroup={{ label: t('magnets.cto.form.reason') }}
                      options={[
                        { label: t('magnets.cto.form.reason0'), value: '0' },
                        { label: t('magnets.cto.form.reason1'), value: '1' },
                        { label: t('magnets.cto.form.reason2'), value: '2' },
                        { label: t('magnets.cto.form.reason3'), value: '3' },
                        { label: t('magnets.cto.form.reason4'), value: '4' }
                      ]}
                    />

                    <Checkbox
                      id="accept"
                      checked={accept}
                      onChange={() => setAccept((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.cto.form.privacy.label'}
                        />
                      }
                    />

                    <Checkbox
                      id="terms"
                      checked={terms}
                      onChange={() => setTerms((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.cto.form.terms.label'}
                          components={{
                            termsLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.termsOfUse)}
                                target="_blank"
                              />
                            ),

                            privacyLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.privacyPolicy)}
                                target="_blank"
                              />
                            )
                          }}
                        />
                      }
                    />

                    <div style={{ display: 'none' }}>
                      {formMethods.formState.isValid
                        ? 'valid: true'
                        : 'valid: false'}
                      {terms ? 'terms: true' : 'terms: false'}
                    </div>

                    <StackItem>
                      <Button
                        label={t('magnets.cto.form.submit')}
                        schema="brand2"
                        startIcon={faCheckCircle}
                        size="xs"
                        isLoading={formMethods.formState.isSubmitting}
                        disabled={
                          !terms ||
                          !accept ||
                          !formMethods.formState.isValid ||
                          reason === '0'
                        }
                      />
                    </StackItem>
                  </Stack>
                </form>
              </FormProvider>
            ) : (
              <Stack direction="column" className="success" alignment="center">
                <Icon icon={faCheckCircleLight} size="6xl" />

                <Header as="h2" size="2xl">
                  {t('magnets.cto.success.title')}
                </Header>

                <Text bold size="l">
                  {t('magnets.cto.success.sub-title')}
                </Text>

                <HtmlTrans
                  ns="magnets"
                  i18nKey="magnets.cto.success.desc"
                  components={{
                    teamLink: (
                      <a href="mailto:team@getmanfred.com" target="_blank" />
                    )
                  }}
                />
              </Stack>
            )}
          </StackItem>
        </S.StyledWrapper>
      </S.StyledWrapperBG>
      <S.StyledMktText>
        <Text bold uppercase size="s">
          {t('magnets.cto.content.sub-title')}
        </Text>
        <Header as="h2" size="2xl">
          {t('magnets.cto.mkt.title')}
        </Header>

        <div>
          <HtmlTrans ns="magnets" i18nKey="magnets.cto.mkt.desc" />
        </div>

        <Link to="form">
          <ButtonLink
            label={t('magnets.cto.form.submit')}
            schema="brand2"
            variant="hard"
            startIcon={faCheckCircle}
            size="xs"
          />
        </Link>
      </S.StyledMktText>
    </Page>
  );
};
